// @flow

// useHeightRestriction is a custom hook designed specifically for PinRep. It
// handles cases where the visual (usually the Pin image) is too short to
// support content and controls in the overlay. Based on the visual's height
// (specified via a ref and use of the useSizeUpdates hook), the action bar may
// be hidden from the overlay.

import { useState } from 'react';
import useSizeUpdates from 'app/common/react/components/PinRep/Hooks/useSizeUpdates';

type HeightRestriction = 'none' | 'header-only';

const HEIGHT_RESERVED_FOR_HEADER = 60;
const HEIGHT_RESERVED_FOR_VISUAL = 40;
const HEIGHT_RESERVED_FOR_ACTION_BAR = 60;

export function getRestrictionForHeight(height: number): HeightRestriction {
  const heightReserved = HEIGHT_RESERVED_FOR_HEADER + HEIGHT_RESERVED_FOR_VISUAL;
  const heightReservedWithActionBar = heightReserved + HEIGHT_RESERVED_FOR_ACTION_BAR;

  if (height < heightReservedWithActionBar) {
    return 'header-only';
  } else {
    return 'none';
  }
}

export default function useHeightRestriction(elementRef: {
  current: ?HTMLDivElement,
}): HeightRestriction {
  // Necessary to maintain state here to force a re-render on height change
  const [heightRestriction, setHeightRestriction] = useState<HeightRestriction>(
    elementRef.current ? getRestrictionForHeight(elementRef.current.clientHeight || 0) : 'none',
  );

  useSizeUpdates(elementRef, ({ height }) => {
    const newHeightRestriction = getRestrictionForHeight(height);

    if (newHeightRestriction !== heightRestriction) {
      setHeightRestriction(newHeightRestriction);
    }
  });

  return heightRestriction;
}
